import { msg } from "@lingui/macro";
import cx from "classnames";
import { useMemo } from "react";

import { selectGlvAndMarketsInfoData } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { selectShiftAvailableMarkets } from "context/SyntheticsStateContext/selectors/shiftSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { isGlvInfo } from "domain/synthetics/markets/glv";
import { getGlvOrMarketAddress } from "domain/synthetics/markets/utils";
import { useLocalizedMap } from "lib/i18n";
import { getByKey } from "lib/objects";

import Tabs from "components/Tabs/Tabs";

import { Operation } from "./types";

const OPERATION_LABELS_GM = {
  [Operation.Deposit]: msg`Buy NTM`,
  [Operation.Withdrawal]: msg`Sell NTM`,
  [Operation.Shift]: msg`Shift NTM`,
};

const OPERATION_LABELS_GLV = {
  [Operation.Deposit]: msg`Buy NTM`,
  [Operation.Withdrawal]: msg`Sell NTM`,
  [Operation.Shift]: msg`Shift NTM`,
};

const OPERATIONS = [Operation.Deposit, Operation.Withdrawal, Operation.Shift];

// const operationClassNames = {
//   [Operation.Deposit]: {
//     active: "!bg-[#1F3445] border-b border-b-green-500",
//     regular: "border-b border-b-[transparent]",
//   },
//   [Operation.Withdrawal]: {
//     active: "!bg-[#392A46] border-b border-b-red-500",
//     regular: "border-b border-b-[transparent]",
//   },
//   [Operation.Shift]: {
//     active: "!bg-[#252B57] border-b border-b-blue-300",
//     regular: "border-b border-b-[transparent]",
//   },
// };

type Props = {
  selectedGlvOrMarketAddress?: string;
  operation: Operation;
  onSetOperation: (operation: Operation) => void;
  isInCurtain?: boolean;
};

export function GmSwapBoxHeader(p: Props) {
  const { selectedGlvOrMarketAddress, operation, onSetOperation, isInCurtain } = p;

  const marketsInfoData = useSelector(selectGlvAndMarketsInfoData);
  const shiftAvailableMarkets = useSelector(selectShiftAvailableMarkets);
  const marketInfo = getByKey(marketsInfoData, selectedGlvOrMarketAddress);

  const availableOperations = useMemo(() => {
    if (shiftAvailableMarkets.length === 0) {
      return [Operation.Deposit, Operation.Withdrawal];
    }

    const isSelectedMarketShiftAvailable = Boolean(
      shiftAvailableMarkets.find((market) => getGlvOrMarketAddress(market) === selectedGlvOrMarketAddress)
    );

    if (!isSelectedMarketShiftAvailable) {
      return [Operation.Deposit, Operation.Withdrawal];
    }

    return OPERATIONS;
  }, [selectedGlvOrMarketAddress, shiftAvailableMarkets]);

  const localizedOperationLabelsGM = useLocalizedMap(OPERATION_LABELS_GM);
  const localizedOperationLabelsGLV = useLocalizedMap(OPERATION_LABELS_GLV);

  const localizedOperationLabels = useMemo(
    () => (marketInfo && isGlvInfo(marketInfo) ? localizedOperationLabelsGLV : localizedOperationLabelsGM),
    [marketInfo, localizedOperationLabelsGM, localizedOperationLabelsGLV]
  );

  const availableOperationsTabsOptions = useMemo(
    () =>
      availableOperations.map((operation) => ({
        value: operation,
        label: localizedOperationLabels[operation],
        // className: operationClassNames[operation],
      })),
    [availableOperations, localizedOperationLabels]
  );

  return (
    <div className={cx({ "p-[1.5rem] pb-0": !isInCurtain })}>
      <Tabs
        options={availableOperationsTabsOptions}
        selectedValue={operation}
        onChange={onSetOperation}
        className={isInCurtain ? undefined : "Exchange-swap-option-tabs"}
        variant="filled"
      />
    </div>
  );
}
